import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useCoreService } from 'core/services/useCoreService';
import { useDataFetcher } from 'shared/http';
import { CentroCosto } from 'core/centrocosto/models/CentroCosto';
import { useNotifications } from 'shared/notifications';
import { FormWithValidation } from 'shared/form';
import { Col } from 'react-bootstrap';
import { Button } from '@orbita-ui/core';
import { Card } from 'shared/components';
import { Tarjeta } from '../../models/Tarjeta';
import { LIST_CARDS_PATH } from '../../routes';
import { SelectOption } from 'shared/form/models/SelectOption';

interface AssociateCardToCostCentreFormProps {
  cardData: Tarjeta;
  onSuccess?: () => void;
}

interface FormValues {
  centroCosto: SelectOption<CentroCosto>;
}

export const AssociateCardToCostCentreForm: React.FC<
  AssociateCardToCostCentreFormProps
> = ({ cardData, onSuccess }) => {
  const navigate = useNavigate();
  const coreService = useCoreService();
  const { addNotification } = useNotifications();
  const methods = useForm<FormValues>({ mode: 'onChange' });

  const [costCentersResponse, fetchCostCenters] = useDataFetcher<CentroCosto[]>();
  const isLoading = costCentersResponse.isLoading;

  useEffect(() => {
    fetchCostCenters(async () => {
      const data = await coreService.getCostCentresChildren();
      return data || [];
    });
  }, [fetchCostCenters, coreService]);

  const costCenterOptions = useMemo((): SelectOption<CentroCosto>[] => {
    if (!costCentersResponse.data) return [];
    return costCentersResponse.data.map((centroCosto: CentroCosto) => ({
      label: centroCosto.nombre,
      value: centroCosto,
    }));
  }, [costCentersResponse.data]);

  const onSubmit = async (formData: FormValues) => {
    const selectedCostCenter: SelectOption<CentroCosto> = formData.centroCosto;

    try {
      const costCenterData = selectedCostCenter.value;

      if (!costCenterData?.numeroCuenta) {
        throw new Error(
          'No se pudo obtener la información de la cuenta del centro de costo.',
        );
      }

      await coreService.associateCardToCostCenter(
        cardData.numeroCuenta,
        costCenterData.id,
      );
      addNotification('Tarjeta asociada correctamente al centro de costo.', 'success');

      if (onSuccess) {
        onSuccess();
      } else {
        navigate(LIST_CARDS_PATH);
      }
    } catch (error) {
      console.error('Error al asociar la tarjeta al centro de costo:', error);
      const message =
        error instanceof Error ? error.message : 'Ocurrió un error inesperado.';
      addNotification(message, 'error');
    }
  };

  return (
    <FormWithValidation onSubmit={onSubmit} reactFormHookMethods={methods}>
      <Card>
        <Card.Title>Datos del centro de costo asociado</Card.Title>
        <Card.Body $padding>
          <Col>
            <label className="form-label">Asociar a</label>
            <FormWithValidation.LegacySelect
              className="mb-2"
              name="centroCosto"
              options={costCenterOptions}
              registerOptions={{
                required: 'Debe seleccionar un centro de costo.',
              }}
              placeholder="Seleccione una opción..."
            />
            <FormWithValidation.Control.Feedback type="invalid" controlId="centroCosto" />

            <div className="text-end mt-3">
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Asociando...' : 'Asociar'}
              </Button>
            </div>
          </Col>
        </Card.Body>
      </Card>
    </FormWithValidation>
  );
};
